<template>
	<div>
		<v-text-field
			v-if="!isMobile"
			v-model="time"
			:append-icon="hideIcon ? '' : 'mdi-clock-outline'"
			:autofocus="autofocus"
			:disabled="disabled"
			:error="invalidTime"
			:label="$t(label)"
			:aria-label="$t('time')"
			:readonly="readonly"
			:required="required"
			:rules="rules"
			@blur="emitBlur($event)"
			@keyup="emitKeyup($event)"
			maxlength="5"
			variant="underlined"
			ref="timeEntry"
		></v-text-field>
		<v-menu v-model="showTimePicker" :close-on-content-click="false" transition="scale-transition" offset-y>
			<template v-slot:activator="{ props }">
				<v-text-field
					v-if="isMobile"
					v-model="time"
					:append-icon="hideIcon ? '' : 'mdi-clock-outline'"
					:disabled="disabled"
					:label="$t(label)"
					:required="required"
					:rules="rules"
					@blur="emitBlur"
					color="primary"
					variant="underlined"
					dense
					readonly
					v-bind="props"
				></v-text-field>
			</template>
			<v-time-picker
				v-model="time"
				@change="$emit('change')"
				@click:hour="handleTimePicker(false)"
				@click:minute="handleTimePicker"
				class="datetime-picker"
				color="primary"
				:min="min"
				format="24hr"
			></v-time-picker>
		</v-menu>
		<v-snackbar v-model="invalidTime" timeout="2000" color="orange darken-2"> {{ $t('enter_valid_time') }} </v-snackbar>
	</div>
</template>

<script>
export default {
	props: {
		modelValue: {
			type: String,
		},

		label: {
			type: String,
			default: 'empty_string',
		},
		autofocus: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		isMobile: {
			type: Boolean,
			default: false,
		},
		hideIcon: {
			type: Boolean,
			default: false,
		},
		required: {
			type: Boolean,
			default: false,
		},
		readonly: {
			type: Boolean,
			default: false,
		},
		rules: {
			type: Array,
			default: () => [],
		},
		min: {
			type: String,
			default: '',
		},
	},
	emits: ['update:modelValue', 'change', 'blur', 'clear', 'getSeconds', 'getTime'],
	data() {
		return {
			time: this.modelValue ?? '',
			invalidTime: false,
			showTimePicker: false,
		};
	},

	methods: {
		handleInput(input) {
			this.time = this.autoConvertToTimeFormat(input);
			if (this.validateTime(this.time)) {
				this.giveTimeToParent(this.time);
			} else {
				this.$emit('update:modelValue', this.time);
			}
		},

		handleTimePicker(hideTimePicker = true) {
			if (hideTimePicker) {
				this.showTimePicker = false;
			}
			if (this.validateTime(this.time)) {
				this.giveTimeToParent(this.time);
			}
		},

		autoConvertToTimeFormat(value) {
			if (value == '') {
				// Use case: Deleted time
				// return ''
				return '';
			}

			if (!value) {
				// Use case: Not defined yet
				// return null
				return value;
			}

			if ((value.length === 3 || value.length === 4) && !value.includes(':') && !value.includes('.') && !value.includes(',')) {
				if (value.length === 3) {
					// e.g. 900 -> 09:00
					const formatted = '0' + value.substring(0, 1) + ':' + value.substring(1, 3);
					return formatted;
				}
				// e.g. 1000 -> 10:00
				const formatted = value.substring(0, 2) + ':' + value.substring(2, 5);
				return formatted;
			}

			if (value.length === 4 && (value.charAt(1) === ':' || value.charAt(1) === ',' || value.charAt(1) === '.')) {
				// e.g. '9.00', '9,00', '9:00' -> '09:00'
				const time = '0' + value.substring(0, 1) + ':' + value.substring(2, 4);
				return time;
			}

			if (value.length === 4 && (value.charAt(2) === ':' || value.charAt(2) === ',' || value.charAt(2) === '.')) {
				// e.g. '19:7', '19.7', '19,7' -> '19:07'
				const time = value.substring(0, 2) + ':0' + value.substring(3, 4);
				return time;
			}

			if (value.length === 3 && (value.charAt(1) === ':' || value.charAt(1) === ',' || value.charAt(1) === '.')) {
				// e.g. '8:7', '8.7', '8,7' -> '08:07'
				const time = '0' + value.substring(0, 1) + ':0' + value.substring(2, 3);
				return time;
			}

			if (value.length === 5) {
				// e.g. '10.00', '10,00', '10:00' -> '10:00'
				const time = value.substring(0, 2) + ':' + value.substring(3, 5);
				return time;
			}

			// Use case: While input
			// e.g. '10', '10:', '10:0'
			return value;
		},

		validateTime(time) {
			if (time == '') {
				// Field is empty => valid -> emit time to parent component
				this.invalidTime = false;
				return true;
			}
			if (!time) {
				// Undefined -> ignore
				return false;
			}
			if (!this.isValidTime(time)) {
				// Time is invalid -> define
				this.invalidTime = true;
				return false;
			}
			if (time.length != 5) {
				// The time is not fully entered -> ignore
				return false;
			}
			// Time is valid -> emit time to parent component
			this.invalidTime = false;
			return true;
		},

		isValidTime(time) {
			const validTimeFormat = /^(?:[01]\d|2[0-9]|3[0-9]):[0-5]\d$/; // Allow time to be above 24h
			return validTimeFormat.test(time);
		},

		convertHHMMToSeconds(time) {
			const [hours, minutes] = time.split(':').map(Number);
			return hours * 3600 + minutes * 60;
		},

		giveTimeToParent(time) {
			if (this.invalidTime) {
				// Invalid time -> abort emit
				return;
			}

			// Valid time (considers also empty fields) -> emit
			let seconds = this.convertHHMMToSeconds(time);
			this.$emit('getSeconds', seconds);
			this.$emit('getTime', time);
			this.$emit('update:modelValue', time);
			this.$emit('change');
		},

		emitBlur(event) {
			if (!this.isMobile) {
				this.handleInput(this.time);
			}
			this.$emit('blur', event);
		},

		emitKeyup(event) {
			this.$emit('keyup', event);
		},

		validate() {
			this.$refs.timeEntry.validate();
		},
	},

	watch: {
		modelValue(v) {
			this.time = v;
		},
		time(v) {
			this.handleTimePicker();
		},
	},

	mounted() {
		if (this.value && this.isValidTime(this.value)) {
			this.time = this.value;
		}
	},
};
</script>
<style>
div:has(> div.datetime-picker) {
	box-shadow: none;
}

div.datetime-picker {
	box-shadow:
		0 5px 5px -3px rgba(0, 0, 0, 0.2),
		0 8px 10px 1px rgba(0, 0, 0, 0.14),
		0 3px 14px 2px rgba(0, 0, 0, 0.12);
}
</style>
